/* eslint-disable @next/next/no-img-element */
import { useInViewport } from 'ahooks';
import { observer } from 'mobx-react';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Link from 'next/link';
import { useRef } from 'react';

import { PdpService } from 'lib/apis/services/PdpSingle';
import useOwnRouter from 'lib/hooks/useOwnRouter';
import { useOwnTranslate } from 'lib/hooks/useOwnTranslate';
import AgentContactInfo from '../../elements/agentContactInfo';
import ContactInfo from './components/contactInfo';
import PdpSimilarListing from './components/pdpSimilarListing';
import PropertyChartHousePrices from './components/propertyChartHousPrices';
import PropertyInfo from './components/propertyInfo';
import { ChevronLeftIcon } from '@heroicons/react/solid';

const PreviousBtn = dynamic(() => import('./components/previousBtn'), {
  ssr: false,
});

interface PdpModuleProps {
  refererUrl?: string;
}

function PdpModule({ refererUrl }: PdpModuleProps) {
  const pricingRef = useRef(null);
  const { t } = useOwnTranslate();
  const [inVisible = true] = useInViewport(pricingRef, {
    threshold: [0, 0.25, 0.5, 0.75, 1],
  });
  const { router } = useOwnRouter();
  const identifier = router.query.id as string;
  //This section is to inform the server that the file has been viewed.
  PdpService.useTrackQuery(identifier);

  const { data, isFetched, isError, isLoading } = PdpService.usePdpSingleQuery(router.query.id as string);

  //get similar product
  const {
    data: similarData,
    isLoading: similarLoading,
    isError: similarError,
    isFetched: similarFetcching,
  } = PdpService.useSimilarListingQuery(router.query.id as string);
  const similarLength = data?.data?.similar?.length;

  return (
    <>
      {data && !data.error && data.data.listing !== null && (
          <div className="relative">
            <div className="container pt-8 pb-6">
              <PreviousBtn refererUrl={refererUrl} />
              <div className="main-details mx-auto md:flex md:space-x-8 rtl:space-x-reverse">
                {/*main content*/}
                <PropertyInfo refererUrl={refererUrl} ref={pricingRef} />
                <div className="left-side-details min-w-[376px] max-w-min relative hidden md:block">
                  <ContactInfo visiblePrice={!inVisible} />
                </div>
              </div>
            </div>
            <div className="container sticky bottom-0 left-0 right-0 z-10 py-3 bg-white border-t lg:hidden border-grey-200">
              <AgentContactInfo Identifier={identifier} className="w-full" fromPage="PDP"/>
            </div>
          </div>
      )}
      {data && (
        <>
          {data?.error !== null || data.data.listing === null && (
            <>
              <Head>
                <title>آگهی موجود نیست</title>
              </Head>
              <div className="container pt-8 pb-6">
                <div className="max-w-full prose text-center">
                  <h2 >این آگهی توسط کاربر حذف شده است.</h2>
                  <Link href={data?.data?.moreSimilarLink || "/" }>
                    <a className="btn btn-lg btn-primary">
                      جستجوی موارد بیشتر
                    </a>
                  </Link>
                  <div className="inline-flex items-center justify-start w-full max-w-full py-2 space-x-4 overflow-x-auto text-gray-800 align-middle border-b border-gray-200 rtl:space-x-reverse snap-x md:overflow-x-hidden">
                    {data?.data?.breadCrumbs?.map((bread, index) => (
                      <div
                        className="flex items-center justify-center space-x-4 rtl:space-x-reverse"
                        key={`p-bread-crumbs-${index}`}
                      >
                        <Link prefetch={false} href={bread.url}>
                          <a className="w-max no-underline">{bread.title}</a>
                        </Link>
                        {index + 1 < data?.data?.breadCrumbs.length && (
                          <ChevronLeftIcon className="w-5" />
                        )}
                      </div>
                    ))}
                  </div>

                </div>
              </div>

            </>
          )}
        </>
      )}
      <div className="container py-6">
        <>
          {(!!similarLength || !!similarData?.data?.length) ? (
            <div>
              <h2 className="px-4 text-display-xs font-semiBold lg:mb-8 lg:px-0">
                {t('global.similarProperties')}
              </h2>
              <PdpSimilarListing
                data={similarData?.data ? similarData?.data : data?.data?.similar}
                isLoading={isLoading}
                isError={isError}
                isFetched={isFetched}
              />
            </div>
          ) : (
            <div className="max-w-full prose text-center">
              <h2>آگهی مشابهی موجود نیست.</h2>
            </div>
          )}
        </>

        {data && !data.error && data.data.listing !== null && (
          <PropertyChartHousePrices locationId={data.data.listing.locationId} locationType={data.data.listing.locationType}/>
        )}
      </div>
    </>
  );
}

export default observer(PdpModule);





